"use client"

import { useContext, useState } from "react"
import PropTypes from "prop-types"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  InputAdornment,
  alpha,
  useTheme,
  Alert,
  Fade,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material"
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  CurrencyExchange as CurrencyIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material"
import { AppContext } from "../../contexts/AppContext"

const Settings = ({ id }) => {
  const { state } = useContext(AppContext)
  const [conversionRate, setConversionRate] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const queryClient = useQueryClient()
  const theme = useTheme()

  const { data, isLoading, error } = useQuery({
    queryKey: ["conversion-rate"],
    queryFn: async () => {
      const response = await axios.get(`/api/v2/partner/${id}`, {
        headers: { Authorization: state.token ? `Bearer ${state.token}` : "" },
      })
      setConversionRate(response.data.pointConversionRate)
      return response.data
    },
  })

  const handleSave = async () => {
    setIsSaving(true)
    setSaveSuccess(false)
    setSaveError(null)

    try {
      await axios.put(
        `/api/v2/partner/${id}/conversion-rate`,
        { conversion_rate: Number(conversionRate) },
        {
          headers: { Authorization: state.token ? `Bearer ${state.token}` : "" },
        },
      )

      queryClient.invalidateQueries(["conversion-rate"])
      setSaveSuccess(true)

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false)
      }, 3000)
    } catch (error) {
      setSaveError(error.message || "Failed to update conversion rate")
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: "auto" }}>
          Error loading settings: {error.message}
        </Alert>
      </Container>
    )
  }

  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: "auto" }}>
          No settings data found. Please try again later.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <SettingsIcon sx={{ fontSize: 28, color: "white" }} />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure your loyalty program settings
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              overflow: "visible",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CurrencyIcon color="primary" />
                  <Typography variant="h5" fontWeight="bold">
                    Points Conversion Rate
                  </Typography>
                </Box>
              }
              subheader="Configure how points are converted to currency value"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: "1px solid",
                borderColor: "divider",
                pb: 2,
              }}
            />

            <CardContent sx={{ p: 3 }}>
              {saveSuccess && (
                <Fade in={saveSuccess}>
                  <Alert
                    icon={<CheckCircleIcon fontSize="inherit" />}
                    severity="success"
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    Conversion rate updated successfully!
                  </Alert>
                </Fade>
              )}

              {saveError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSaveError(null)}>
                  {saveError}
                </Alert>
              )}

              <Box sx={{ mb: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.info.main, 0.2),
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <InfoIcon color="info" sx={{ mr: 1.5, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      About Points Conversion
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The conversion rate determines the monetary value of each loyalty point. For example, if the
                      conversion rate is 0.1, then 1 point is worth $0.10 in your currency.
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Current Conversion Rate
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Chip
                    label="1 Point ="
                    color="primary"
                    sx={{
                      mr: 2,
                      fontWeight: "bold",
                      height: 32,
                    }}
                  />
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {data.pointConversionRate}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                    currency units
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Update Conversion Rate
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Value of 1 Point"
                      type="number"
                      value={conversionRate || ""}
                      onChange={(e) => setConversionRate(e.target.value)}
                      variant="outlined"
                      placeholder="Enter conversion rate"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencyIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                      helperText="Enter the monetary value of a single point"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Tooltip title="Save the new conversion rate">
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={handleSave}
                        disabled={isSaving || conversionRate === null}
                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          "&:hover": {
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                          },
                        }}
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </Button>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              height: "100%",
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <CardHeader
              title="Points Calculator"
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Use this calculator to see how points convert to currency value with your current rate.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Points"
                  type="number"
                  defaultValue={100}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.success.main, 0.2),
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Value
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color={theme.palette.success.dark}>
                    {(100 * (conversionRate || 0)).toFixed(2)}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Common Conversions
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">10 points</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    = {(10 * (conversionRate || 0)).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">50 points</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    = {(50 * (conversionRate || 0)).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">500 points</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    = {(500 * (conversionRate || 0)).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">1000 points</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    = {(1000 * (conversionRate || 0)).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

Settings.propTypes = {
  id: PropTypes.string.isRequired,
}

export default Settings
